import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Button, H4, Spinner } from "@blueprintjs/core";

import { refreshUserAuth } from "@api";
import { LinkButton } from "@components";
import { UserInvite, UserInviteStatus } from "@types";
import { withToaster } from "@utils";

import { fetchUserInvites, updateUserInvite } from "./api";

const ActionsContainer = styled.div`
  margin: 10vh auto 1em auto;
  padding: 1em;
`;

const ActionPanel = styled.div`
  margin: 1em auto 1em auto;
  padding: 1em;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 3px;
  min-width: 300px;
  max-width: 600px;
`;

export const OrganizationInvitePanel = () => {
  const [userInvites, setUserInvites] = useState<UserInvite[]>([]);

  const history = useHistory();

  useEffect(() => {
    let isMounted = true;
    withToaster(
      () =>
        fetchUserInvites().then((user_invites) => {
          if (!isMounted) return;
          setUserInvites(user_invites);
        }),
      (err) => {
        new Promise((resolve) => setTimeout(resolve, 5000)).then(() => {
          history.go(0);
        });
        return `Failed to load invites: ${err.body?.error}`;
      }
    )();

    // Abort any callbacks/promises on component unmounting
    return () => {
      isMounted = false;
    };
  }, [history]);

  const handleChoice =
    (
      newStatus: UserInviteStatus.Rejected | UserInviteStatus.Joined,
      userInvite: UserInvite
    ) =>
    () => {
      const newUserInvite = { ...userInvite, status: newStatus };
      updateUserInvite(newUserInvite)
        .then(() => {
          return refreshUserAuth();
        })
        .then(() => {
          history.go(0);
        });
    };

  if (!userInvites || userInvites.length < 1) {
    return <Spinner intent="primary" />;
  }

  // Pick the 1st user invite and show it to user.
  // By default, there can only be 1, but this is just
  // defensive future-proofing
  const userInvite = userInvites[0];

  return (
    <ActionsContainer>
      <ActionPanel>
        <H4>
          <span role="img" aria-label=":wave:">
            ✉️
          </span>{" "}
          You have been invited to join an organization:
        </H4>
        <p>
          You have been invited by{" "}
          {userInvite.created_by_user_id || "your colleague"} to join their
          organization
          <strong>
            {userInvite.organization
              ? " " +
                (userInvite.organization.name || userInvite.organization.slug)
              : ""}
          </strong>
          . If you accept, you will be able to collaborate with them via the
          Semgrep web app.
        </p>

        <Button
          onClick={handleChoice(UserInviteStatus.Joined, userInvite)}
          intent="success"
          style={{ margin: "24px 8px", width: "95%", height: "48px" }}
        >
          Accept the invite
        </Button>

        <div style={{ textAlign: "center" }}>
          <LinkButton
            onClick={handleChoice(UserInviteStatus.Rejected, userInvite)}
            style={{ fontSize: "small" }}
          >
            I do not want to join this organization.
          </LinkButton>
        </div>
      </ActionPanel>
    </ActionsContainer>
  );
};
